import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Edit2, Trash2, Copy, ChevronDown, ChevronRight,
  Search, Filter, X, Check, Loader2, Info, AlertTriangle,
  Settings, BookTemplate, Layers, Globe, Building2, Download,
  RefreshCw, Users
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonTable, SkeletonKPIGrid } from '../components/Skeleton';
import EmptyState, { NoDataState } from '../components/EmptyState';

// ==========================================
// TEMPLATE TYPE ICONS
// ==========================================

const TYPE_ICONS = {
  REQUIREMENT: FileText,
  ACCEPTANCE_CRITERIA: Check,
  RISK: AlertTriangle,
  CONSTRAINT: Settings,
  WBS_PACKAGE: Layers,
  PMO_PHASE: BookTemplate,
  BASELINE: FileText,
  DEPENDENCY: Layers,
  RESOURCE: Users,
};

const TYPE_COLORS = {
  REQUIREMENT: 'bg-blue-100 text-blue-700 border-blue-200',
  ACCEPTANCE_CRITERIA: 'bg-green-100 text-green-700 border-green-200',
  RISK: 'bg-red-100 text-red-700 border-red-200',
  CONSTRAINT: 'bg-amber-100 text-amber-700 border-amber-200',
  WBS_PACKAGE: 'bg-purple-100 text-purple-700 border-purple-200',
  PMO_PHASE: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  BASELINE: 'bg-gray-100 text-gray-700 border-gray-200',
  DEPENDENCY: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  RESOURCE: 'bg-pink-100 text-pink-700 border-pink-200',
};

// ==========================================
// TEMPLATE EDITOR MODAL
// ==========================================

const TemplateEditorModal = ({
  isOpen,
  onClose,
  template,
  templateTypes,
  onSave,
  isGlobal = false,
  isSaving = false
}) => {
  const [formData, setFormData] = useState({
    template_type: '',
    category: '',
    name: '',
    description: '',
    fields: [],
    is_active: true,
    order: 0,
  });
  
  const [newField, setNewField] = useState({
    key: '',
    label: '',
    type: 'text',
    required: false,
    hint: '',
    options: '',
  });

  useEffect(() => {
    if (template) {
      setFormData({
        template_type: template.template_type || '',
        category: template.category || '',
        name: template.name || '',
        description: template.description || '',
        fields: template.fields || [],
        is_active: template.is_active !== false,
        order: template.order || 0,
      });
    } else {
      setFormData({
        template_type: '',
        category: '',
        name: '',
        description: '',
        fields: [],
        is_active: true,
        order: 0,
      });
    }
  }, [template, isOpen]);

  const handleAddField = () => {
    if (!newField.key || !newField.label) {
      toast.error('Field key and label are required');
      return;
    }
    
    const field = {
      ...newField,
      options: newField.type === 'select' && newField.options
        ? newField.options.split(',').map(o => o.trim()).filter(Boolean)
        : undefined,
    };
    
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, field]
    }));
    
    setNewField({
      key: '',
      label: '',
      type: 'text',
      required: false,
      hint: '',
      options: '',
    });
  };

  const handleRemoveField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.template_type || !formData.category || !formData.name) {
      toast.error('Template type, category, and name are required');
      return;
    }
    onSave(formData);
  };

  const availableCategories = formData.template_type && templateTypes[formData.template_type]
    ? templateTypes[formData.template_type].categories
    : [];

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="template-editor-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {template ? 'Edit Template' : 'Create Template'}
              {isGlobal && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  Global
                </span>
              )}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Type *
                </label>
                <select
                  value={formData.template_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, template_type: e.target.value, category: '' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  required
                  disabled={!!template}
                  data-testid="template-type-select"
                >
                  <option value="">Select Type...</option>
                  {Object.entries(templateTypes).map(([key, val]) => (
                    <option key={key} value={key}>{val.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  required
                  disabled={!formData.template_type}
                  data-testid="template-category-select"
                >
                  <option value="">Select Category...</option>
                  {availableCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                placeholder="e.g., User Story, Gherkin AC, Schedule Risk"
                required
                data-testid="template-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none resize-none"
                rows={2}
                placeholder="Brief description of when to use this template"
                data-testid="template-description-input"
              />
            </div>

            {/* Fields Section */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-3">Template Fields</h4>
              
              {/* Existing fields */}
              {formData.fields.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.fields.map((field, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700 flex-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-gray-200 rounded">
                        {field.type}
                      </span>
                      <span className="text-xs text-gray-500">{field.key}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveField(index)}
                        className="p-1 hover:bg-red-100 rounded"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new field */}
              <div className="bg-blue-50 rounded-lg p-3 space-y-3">
                <p className="text-xs font-medium text-blue-700 uppercase">Add Field</p>
                <div className="grid grid-cols-4 gap-2">
                  <input
                    type="text"
                    value={newField.key}
                    onChange={(e) => setNewField(prev => ({ ...prev, key: e.target.value.replace(/\s/g, '_').toLowerCase() }))}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                    placeholder="field_key"
                  />
                  <input
                    type="text"
                    value={newField.label}
                    onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                    placeholder="Field Label"
                  />
                  <select
                    value={newField.type}
                    onChange={(e) => setNewField(prev => ({ ...prev, type: e.target.value }))}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="select">Select</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="date">Date</option>
                  </select>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={newField.required}
                        onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                        className="rounded"
                      />
                      Req
                    </label>
                    <button
                      type="button"
                      onClick={handleAddField}
                      className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {newField.type === 'select' && (
                  <input
                    type="text"
                    value={newField.options}
                    onChange={(e) => setNewField(prev => ({ ...prev, options: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                    placeholder="Options (comma separated): Option 1, Option 2, Option 3"
                  />
                )}
                <input
                  type="text"
                  value={newField.hint}
                  onChange={(e) => setNewField(prev => ({ ...prev, hint: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  placeholder="Hint text (optional)"
                />
              </div>
            </div>

            {/* Settings */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Order:</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  min="0"
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              data-testid="save-template-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {isSaving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const TemplateLibraryPage = () => {
  const { user } = useAuth();
  const tenantId = user?.tenant?.id || user?.tenant_id;
  const isSystemAdmin = user?.roles?.some(r => r.role_key === 'sysadmin');

  // State
  const [loading, setLoading] = useState(true);
  const [templateTypes, setTemplateTypes] = useState({});
  const [globalTemplates, setGlobalTemplates] = useState([]);
  const [tenantTemplates, setTenantTemplates] = useState([]);
  const [mergedTemplates, setMergedTemplates] = useState([]);
  const [groupedTemplates, setGroupedTemplates] = useState({});
  
  // Filters
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('merged'); // 'merged', 'global', 'tenant'
  
  // UI State
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditingGlobal, setIsEditingGlobal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load data
  useEffect(() => {
    loadTemplateTypes();
    loadTemplates();
  }, [tenantId, viewMode]);

  const loadTemplateTypes = async () => {
    try {
      const res = await api.getTemplateTypes();
      setTemplateTypes(res.data.types || {});
    } catch (err) {
      console.error('Failed to load template types:', err);
    }
  };

  const loadTemplates = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      if (viewMode === 'merged') {
        const res = await api.getMergedTemplates(tenantId, {
          template_type: selectedType || undefined,
          category: selectedCategory || undefined,
        });
        setMergedTemplates(res.data.templates || []);
        setGroupedTemplates(res.data.grouped || {});
      } else if (viewMode === 'global') {
        const res = await api.listGlobalTemplates({
          template_type: selectedType || undefined,
          category: selectedCategory || undefined,
        });
        setGlobalTemplates(res.data.templates || []);
      } else if (viewMode === 'tenant') {
        const res = await api.listTenantTemplates(tenantId, {
          template_type: selectedType || undefined,
          category: selectedCategory || undefined,
        });
        setTenantTemplates(res.data.templates || []);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Seed defaults (admin only)
  const handleSeedDefaults = async () => {
    if (!window.confirm('Seed default global templates? This will only add templates if none exist.')) return;
    try {
      const res = await api.seedDefaultTemplates(user.id);
      toast.success(res.data.message);
      loadTemplates();
    } catch (err) {
      console.error('Failed to seed defaults:', err);
      toast.error('Failed to seed default templates');
    }
  };

  // CRUD handlers
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsEditingGlobal(viewMode === 'global');
    setShowEditorModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsEditingGlobal(template.source === 'global');
    setShowEditorModal(true);
  };

  const handleSaveTemplate = async (formData) => {
    setIsSaving(true);
    try {
      if (editingTemplate) {
        // Update existing
        if (isEditingGlobal) {
          await api.updateGlobalTemplate(editingTemplate.id, formData, user.id);
        } else {
          await api.updateTenantTemplate(editingTemplate.id, tenantId, user.id, formData);
        }
        toast.success('Template updated successfully');
      } else {
        // Create new
        if (isEditingGlobal) {
          await api.createGlobalTemplate(formData, user.id);
        } else {
          await api.createTenantTemplate(tenantId, user.id, formData);
        }
        toast.success('Template created successfully');
      }
      setShowEditorModal(false);
      loadTemplates();
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    try {
      if (template.source === 'global') {
        await api.deleteGlobalTemplate(template.id, user.id);
      } else {
        await api.deleteTenantTemplate(template.id, tenantId, user.id);
      }
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      toast.error('Failed to delete template');
    }
  };

  const handleCloneToTenant = async (globalTemplate) => {
    const newName = window.prompt('Enter name for the tenant copy:', `${globalTemplate.name} (Custom)`);
    if (!newName) return;
    try {
      await api.cloneGlobalToTenant(globalTemplate.id, tenantId, user.id, newName);
      toast.success('Template cloned to tenant');
      loadTemplates();
    } catch (err) {
      console.error('Failed to clone template:', err);
      toast.error(err.response?.data?.detail || 'Failed to clone template');
    }
  };

  // Toggle type expansion
  const toggleTypeExpand = (typeKey) => {
    setExpandedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeKey)) {
        newSet.delete(typeKey);
      } else {
        newSet.add(typeKey);
      }
      return newSet;
    });
  };

  // Filter templates
  const getFilteredTemplates = useCallback(() => {
    let templates = [];
    if (viewMode === 'merged') {
      templates = mergedTemplates;
    } else if (viewMode === 'global') {
      templates = globalTemplates;
    } else {
      templates = tenantTemplates;
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      templates = templates.filter(t =>
        t.name?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.category?.toLowerCase().includes(query)
      );
    }

    return templates;
  }, [viewMode, mergedTemplates, globalTemplates, tenantTemplates, searchQuery]);

  const filteredTemplates = getFilteredTemplates();

  // Stats
  const stats = {
    total: filteredTemplates.length,
    global: viewMode === 'merged' ? filteredTemplates.filter(t => t.source === 'global').length : globalTemplates.length,
    tenant: viewMode === 'merged' ? filteredTemplates.filter(t => t.source === 'tenant').length : tenantTemplates.length,
    types: Object.keys(groupedTemplates).length,
  };

  if (loading && !filteredTemplates.length) {
    return (
      <div className="p-6" data-testid="loading-skeleton">
        <div className="mb-6">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse mb-2" />
          <div className="h-4 bg-gray-200 rounded w-96 animate-pulse" />
        </div>
        <SkeletonKPIGrid count={4} className="mb-6" />
        <SkeletonTable rows={8} columns={5} />
      </div>
    );
  }

  return (
    <PageErrorBoundary pageName="Template Library">
      <div className="p-6 max-w-7xl mx-auto" data-testid="template-library-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Template Library</h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage templates for requirements, acceptance criteria, risks, WBS, and more
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isSystemAdmin && viewMode === 'global' && (
              <button
                onClick={handleSeedDefaults}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                data-testid="seed-defaults-btn"
              >
                <Download className="w-4 h-4" />
                Seed Defaults
              </button>
            )}
            <button
              onClick={() => loadTemplates()}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleCreateTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
              data-testid="create-template-btn"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <WidgetErrorBoundary widgetName="Stats">
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1">Total Templates</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <Globe className="w-4 h-4" /> Global
              </div>
              <div className="text-2xl font-bold text-blue-600">{stats.global}</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <Building2 className="w-4 h-4" /> Tenant
              </div>
              <div className="text-2xl font-bold text-green-600">{stats.tenant}</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1">Template Types</div>
              <div className="text-2xl font-bold text-purple-600">{stats.types || Object.keys(templateTypes).length}</div>
            </div>
          </div>
        </WidgetErrorBoundary>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            {/* View Mode */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('merged')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'merged' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="view-merged"
              >
                Merged
              </button>
              {isSystemAdmin && (
                <button
                  onClick={() => setViewMode('global')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'global' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="view-global"
                >
                  <Globe className="w-4 h-4 inline mr-1" />
                  Global
                </button>
              )}
              <button
                onClick={() => setViewMode('tenant')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'tenant' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="view-tenant"
              >
                <Building2 className="w-4 h-4 inline mr-1" />
                Tenant
              </button>
            </div>

            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setSelectedCategory('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="type-filter"
            >
              <option value="">All Types</option>
              {Object.entries(templateTypes).map(([key, val]) => (
                <option key={key} value={key}>{val.name}</option>
              ))}
            </select>

            {/* Category Filter */}
            {selectedType && templateTypes[selectedType] && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                data-testid="category-filter"
              >
                <option value="">All Categories</option>
                {templateTypes[selectedType].categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}

            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Clear Filters */}
            {(selectedType || selectedCategory || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedType('');
                  setSelectedCategory('');
                  setSearchQuery('');
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Templates Grid/List */}
        {filteredTemplates.length === 0 ? (
          <NoDataState
            title="No templates found"
            description={
              searchQuery
                ? "No templates match your search. Try adjusting your filters."
                : "Get started by creating your first template or seeding defaults."
            }
          />
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            {/* Grouped View (when merged and no filters) */}
            {viewMode === 'merged' && !searchQuery && !selectedType && Object.keys(groupedTemplates).length > 0 ? (
              <div className="divide-y divide-gray-200">
                {Object.entries(groupedTemplates).map(([typeKey, typeData]) => {
                  const TypeIcon = TYPE_ICONS[typeKey] || FileText;
                  const isExpanded = expandedTypes.has(typeKey);
                  const templateCount = Object.values(typeData.categories || {}).flat().length;

                  return (
                    <div key={typeKey}>
                      {/* Type Header */}
                      <button
                        onClick={() => toggleTypeExpand(typeKey)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                        data-testid={`type-header-${typeKey}`}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                          <span className={`p-1.5 rounded ${TYPE_COLORS[typeKey] || 'bg-gray-100'}`}>
                            <TypeIcon className="w-4 h-4" />
                          </span>
                          <span className="font-medium text-gray-900">
                            {typeData.info?.name || typeKey}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">{templateCount} templates</span>
                      </button>

                      {/* Type Contents */}
                      {isExpanded && (
                        <div className="divide-y divide-gray-100">
                          {Object.entries(typeData.categories || {}).map(([category, templates]) => (
                            <div key={category} className="px-4 py-2">
                              <div className="text-xs font-medium text-gray-500 uppercase mb-2 pl-8">
                                {category}
                              </div>
                              <div className="space-y-1">
                                {templates.map(template => (
                                  <TemplateRow
                                    key={template.id}
                                    template={template}
                                    onEdit={handleEditTemplate}
                                    onDelete={handleDeleteTemplate}
                                    onClone={handleCloneToTenant}
                                    canEdit={template.source === 'tenant' || isSystemAdmin}
                                    canClone={template.source === 'global'}
                                    indented
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Flat List View */
              <div className="divide-y divide-gray-200">
                <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase flex items-center">
                  <span className="flex-1">Template</span>
                  <span className="w-32">Type</span>
                  <span className="w-28">Category</span>
                  <span className="w-24">Source</span>
                  <span className="w-20 text-right">Actions</span>
                </div>
                {filteredTemplates.map(template => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    onEdit={handleEditTemplate}
                    onDelete={handleDeleteTemplate}
                    onClone={handleCloneToTenant}
                    canEdit={template.source === 'tenant' || isSystemAdmin}
                    canClone={template.source === 'global'}
                    showDetails
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Editor Modal */}
        <TemplateEditorModal
          isOpen={showEditorModal}
          onClose={() => setShowEditorModal(false)}
          template={editingTemplate}
          templateTypes={templateTypes}
          onSave={handleSaveTemplate}
          isGlobal={isEditingGlobal}
          isSaving={isSaving}
        />
      </div>
    </PageErrorBoundary>
  );
};

// ==========================================
// TEMPLATE ROW COMPONENT
// ==========================================

const TemplateRow = ({
  template,
  onEdit,
  onDelete,
  onClone,
  canEdit,
  canClone,
  showDetails = false,
  indented = false
}) => {
  const TypeIcon = TYPE_ICONS[template.template_type] || FileText;

  return (
    <div
      className={`flex items-center px-4 py-3 hover:bg-gray-50 transition-colors ${indented ? 'pl-12' : ''}`}
      data-testid={`template-row-${template.id}`}
    >
      {/* Name & Description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{template.name}</span>
          {!template.is_active && (
            <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
              Inactive
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-sm text-gray-500 truncate">{template.description}</p>
        )}
        {!showDetails && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-400">{template.fields?.length || 0} fields</span>
          </div>
        )}
      </div>

      {/* Type */}
      {showDetails && (
        <div className="w-32">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[template.template_type] || 'bg-gray-100'}`}>
            <TypeIcon className="w-3 h-3" />
            {template.template_type?.replace('_', ' ')}
          </span>
        </div>
      )}

      {/* Category */}
      {showDetails && (
        <div className="w-28 text-sm text-gray-600">
          {template.category}
        </div>
      )}

      {/* Source */}
      <div className={showDetails ? "w-24" : "w-20"}>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
          template.source === 'global'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-green-100 text-green-700'
        }`}>
          {template.source === 'global' ? (
            <Globe className="w-3 h-3" />
          ) : (
            <Building2 className="w-3 h-3" />
          )}
          {template.source}
        </span>
      </div>

      {/* Actions */}
      <div className="w-20 flex items-center justify-end gap-1">
        {canClone && (
          <button
            onClick={() => onClone(template)}
            className="p-1.5 hover:bg-gray-100 rounded"
            title="Clone to Tenant"
            data-testid={`clone-${template.id}`}
          >
            <Copy className="w-4 h-4 text-gray-500" />
          </button>
        )}
        {canEdit && (
          <>
            <button
              onClick={() => onEdit(template)}
              className="p-1.5 hover:bg-gray-100 rounded"
              title="Edit"
              data-testid={`edit-${template.id}`}
            >
              <Edit2 className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => onDelete(template)}
              className="p-1.5 hover:bg-red-100 rounded"
              title="Delete"
              data-testid={`delete-${template.id}`}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TemplateLibraryPage;
